/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import './overwriteValues.tpl.html';

(function () {
  /**
   * This directive displays the controls for configuring the overwrite fields for an object restore.
   */
  angular
    .module('bulkRestore.overwriteValues', ['services.error', 'bulkRestore.saveModal'])
    .directive('sbsfBulkRestoreOverwriteValues', directive)
    .controller('BulkRestoreOverwriteValuesCtrl', controller);

  /**
   * Defines the sbsf-bulk-restore-overwrite-fields directive.
   *
   * @returns {Object} - The directive settings
   */
  function directive() {
    return {
      controller: 'BulkRestoreOverwriteValuesCtrl',
      templateUrl: 'bulkRestore/settings/overwriteValues.tpl.html',
      restrict: 'E',
      scope: {}
    };
  }

  controller.$inject = [
    '$filter',
    '$uibModal',
    '$q',
    '$rootScope',
    '$scope',
    '$state',
    'BulkRestoreService',
    'ErrorService',
    'WizardDirection',
    'WizardService',
    'RESTORE_OVERWRITE_STRATEGIES',
    'SALESFORCE_FIELD_TYPES',
    'confirmModalUtils'
  ];

  /**
   * The controller for the directive.
   */
  function controller(
    $filter,
    $uibModal,
    $q,
    $rootScope,
    $scope,
    $state,
    BulkRestoreService,
    ErrorService,
    WizardDirection,
    WizardService,
    RESTORE_OVERWRITE_STRATEGIES,
    SALESFORCE_FIELD_TYPES,
    confirmModalUtils
  ) {
    if (!$state.params.bulkRestoreData) {
      throw new Error('bulkRestoreData is required');
    }

    // Published functions
    $scope.addActivePicklistLabels = addActivePicklistLabels;
    $scope.addEntry = addEntry;
    $scope.autoSelectField = autoSelectField;
    $scope.autoSelectObjectType = autoSelectObjectType;
    $scope.calculateDisabledObjectTypes = calculateDisabledObjectTypes;
    $scope.calculateSelectedFieldsByObjectType = calculateSelectedFieldsByObjectType;
    $scope.canAddEntry = canAddEntry;
    $scope.canBeRandom = canBeRandom;
    $scope.confirmUnsavedPreset = confirmUnsavedPreset;
    $scope.deletePreset = deletePreset;
    $scope.deleteEntry = deleteEntry;
    $scope.fieldSelected = fieldSelected;
    $scope.findField = findField;
    $scope.findPicklistItem = findPicklistItem;
    $scope.hasPresets = hasPresets;
    $scope.hasRestorableObjectTypes = hasRestorableObjectTypes;
    $scope.hasValues = hasValues;
    $scope.initOverwriteValueEntry = initOverwriteValueEntry;
    $scope.initOverwriteValueEntries = initOverwriteValueEntries;
    $scope.isBlank = isBlank;
    $scope.isBooleanFieldTrue = isBooleanFieldTrue;
    $scope.isFieldValueShown = isFieldValueShown;
    $scope.isPresetModified = isPresetModified;
    $scope.loadAllFields = loadAllFields;
    $scope.loadObjectTypes = loadObjectTypes;
    $scope.loadPresets = loadPresets;
    $scope.multiPickListSelectionChanged = multiPickListSelectionChanged;
    $scope.objectTypeSelected = objectTypeSelected;
    $scope.pickListValueSelected = pickListValueSelected;
    $scope.savePreset = savePreset;
    $scope.selectionChanged = selectionChanged;
    $scope.selectPreset = selectPreset;
    $scope.setUsingPreset = setUsingPreset;
    $scope.shouldShowEntries = shouldShowEntries;
    $scope.strategySelected = strategySelected;
    $scope.toggleBooleanField = toggleBooleanField;
    $scope.valuesAreValid = valuesAreValid;

    var i18n = $filter('i18n');
    var LOADING = i18n('LOADING_MSG_WITH_DOTS');
    var bulkRestoreData = $state.params.bulkRestoreData;
    $scope.bulkRestoreData = bulkRestoreData;

    var strategyNameToLabelMap = {};
    _.each(RESTORE_OVERWRITE_STRATEGIES, function (strategyName) {
      strategyNameToLabelMap[strategyName] = i18n('RESTORE_OVERWRITE_STRATEGY_' + strategyName);
    });
    var strategyLabelToNameMap = _.invert(strategyNameToLabelMap);

    // Anything we pass to a directive needs to be stored in a model that isn't the scope so that the watchers work as
    // we expect.
    var model = {
      allObjectTypes: [LOADING],
      creatingNew: true,
      currentPreset: null,
      disabledObjectTypes: [],
      constantStrategyLabel: strategyNameToLabelMap[RESTORE_OVERWRITE_STRATEGIES.CONSTANT],
      initializing: true,
      initializingPreset: false,
      presets: [],
      strategyLabels: _.keys(strategyLabelToNameMap),
      selectedFieldsByObjectType: {},
      usingPreset: false
    };
    $scope.model = model;

    // Hook into the current wizard page to confirm loss of unsaved changes if the user leaves the page
    var page = WizardService.getCurrentPage();
    page.setConfirmLeave(function (direction) {
      var goingToNext = direction === WizardDirection.FORWARD;
      return confirmUnsavedPreset(goingToNext);
    });

    model.initializing = true;
    loadObjectTypes()
      .then(loadPresets)
      .then(initOverwriteValueEntries)
      .then(function () {
        selectionChanged();

        // Watch for this section to be shown
        $scope.$watch(
          function () {
            return bulkRestoreData.isOverwritingValues();
          },
          function (newValue) {
            // If the list is empty add a default row
            if (newValue && !hasValues()) {
              return addEntry();
            }
          }
        );

        // Watch for the "include children" setting to be changed
        $scope.$watch(
          function () {
            return bulkRestoreData.isIncludingAllChildren();
          },
          function (newValue, oldValue) {
            if (newValue !== oldValue) {
              return loadObjectTypes();
            }
          }
        );
      })
      .catch(function (err) {
        ErrorService.report(err);
      })
      .finally(function () {
        model.initializing = false;
      });

    /**
     * Adds an entry to the overwrite values.
     *
     * @returns {Promise} a promise that resolves to the new entry.
     */
    function addEntry() {
      var newEntry = bulkRestoreData.addOverwriteValueEntry();
      return initOverwriteValueEntry(newEntry)
        .then(function () {
          return autoSelectObjectType(newEntry);
        })
        .then(function () {
          return newEntry;
        });
    }

    /**
     * Adds the active labels from a set of picklist values to an array.
     *
     * @param {Array} dstArray - the array to be added to
     * @param {Array} picklistValues - the picklist values to be added
     */
    function addActivePicklistLabels(dstArray, labels, picklistValues) {
      _.each(picklistValues, function (picklistValue) {
        if (picklistValue.active) {
          dstArray.push(`${picklistValue.label}-${picklistValue.value}`);
          labels.push(picklistValue.label);
        }
      });
    }

    /**
     * Automatically selects a field for an overwrite value if there is only one unselected field remaining for that
     * object type.
     *
     * @param {object} ovEntry - the overwrite value entry
     */
    function autoSelectField(ovEntry) {
      var allFields = ovEntry.allFieldNames;
      var selectedFields = model.selectedFieldsByObjectType[ovEntry.objectType] || [];
      var totalCount = allFields.length;
      var selectedCount = selectedFields.length;

      if (totalCount - selectedCount === 1) {
        var unselectedFields = _.difference(ovEntry.allFieldNames, selectedFields);
        $scope.fieldSelected(ovEntry, unselectedFields[0]);
      }
    }

    /**
     * Automatically selects a type for an overwrite value if there is only one available type remaining.
     *
     * @param {object} ovEntry - the overwrite value entry
     * @returns {Promise} a promise that resolves when the work is done.
     */
    function autoSelectObjectType(ovEntry) {
      var totalCount = model.allObjectTypes.length;
      var disabledCount = model.disabledObjectTypes.length;

      if (totalCount - disabledCount === 1) {
        var availableTypes = _.difference(model.allObjectTypes, model.disabledObjectTypes);
        return $scope.objectTypeSelected(ovEntry, availableTypes[0]);
      } else {
        return $q.when();
      }
    }

    /**
     * For each type used by an overwrite field, this function builds a list of the fields selected for that type. This
     * is used to prevent the same field from being configured more than once.
     */
    function calculateSelectedFieldsByObjectType() {
      // The keys will be object types and the values will be field name arrays.
      model.selectedFieldsByObjectType = {};

      _.each(bulkRestoreData.getOverwriteValueEntries(), function (ovEntry) {
        if (!_.has(model.selectedFieldsByObjectType, ovEntry.objectType)) {
          model.selectedFieldsByObjectType[ovEntry.objectType] = [];
        }

        if (ovEntry.objectField) {
          model.selectedFieldsByObjectType[ovEntry.objectType].push(ovEntry.objectField);
        }
      });
    }

    /**
     * Determines which object types should not be selectable. This is the case when all the fields in an object type
     * have already been used.
     */
    function calculateDisabledObjectTypes() {
      model.disabledObjectTypes = [];

      _.each(bulkRestoreData.getOverwriteValueEntries(), function (ovEntry) {
        // If the number of selected fields equals the total number of fields this one is going to be disabled
        var totalCount = _.size(ovEntry.allFields);
        var selectedCount = _.size(model.selectedFieldsByObjectType[ovEntry.objectType]);
        if (selectedCount === totalCount && !_.contains(model.disabledObjectTypes, ovEntry.objectType)) {
          model.disabledObjectTypes.push(ovEntry.objectType);
        }
      });

      _.each(bulkRestoreData.getOverwriteValueEntries(), function (ovEntry) {
        // For each overwrite type we disable the one that is currently selected plus the others that are disabled
        ovEntry.disabledObjectTypes = _.union(model.disabledObjectTypes, [ovEntry.objectType]);
      });
    }

    /**
     * Determines whether another overwrite value entry can be added.
     *
     * @returns {boolean} true if another overwrite value entry can be added.
     */
    function canAddEntry() {
      return model.disabledObjectTypes.length < model.allObjectTypes.length;
    }

    /**
     * Determines whether we support the random value strategy for a particular overwrite value field type.
     *
     * @param {object} overwriteValue - the overwrite value whose field type is to be checked.
     */
    function canBeRandom(overwriteValue) {
      switch (overwriteValue.fieldType) {
        case SALESFORCE_FIELD_TYPES.BOOLEAN:
        case SALESFORCE_FIELD_TYPES.DATE:
        case SALESFORCE_FIELD_TYPES.DATETIME:
        case SALESFORCE_FIELD_TYPES.DOUBLE:
        case SALESFORCE_FIELD_TYPES.EMAIL:
        case SALESFORCE_FIELD_TYPES.INT:
        case SALESFORCE_FIELD_TYPES.MULTIPICKLIST:
        case SALESFORCE_FIELD_TYPES.PICKLIST:
        case SALESFORCE_FIELD_TYPES.STRING:
        case SALESFORCE_FIELD_TYPES.TIME:
          return true;

        default:
          return false;
      }
    }

    /**
     * Deletes the current preset.
     *
     * @returns {Promise} a promise that resolves when the work is done
     */
    function deletePreset() {
      const promise = confirmModalUtils.openConfirmModal({
        confirmText: i18n('DELETE_PRESET', model.currentPreset.name),
        titleTextKey: 'DELETE',
        doNotCatch: true
      });

      return promise.then(function (confirmed) {
        if (confirmed) {
          $rootScope.showLoadingMask = true;
          return BulkRestoreService.deleteOverwriteValuePreset(model.currentPreset.name)
            .then(function (presets) {
              model.presets = presets;
              model.currentPreset = null;
              bulkRestoreData.clearOverwriteValueEntries();

              // If there are no remaining presets switch to Create New
              if (_.size(presets) === 0) {
                $scope.setUsingPreset(false);
              }
            })
            .catch(function (err) {
              ErrorService.report(err);
            })
            .finally(function () {
              $rootScope.showLoadingMask = false;
            });
        }
      });
    }

    /**
     * Utility to delete a row from the list
     *
     * @param {object} overwriteValue - the overwrite value object to be deleted
     */
    function deleteEntry(overwriteValue) {
      bulkRestoreData.removeOverwriteValueEntry(overwriteValue.objectType, overwriteValue.objectField);

      selectionChanged();
    }

    /**
     * Called when a new field is selected in an overwrite value row.
     *
     * @param {object} ovEntry - The overwrite value entry.
     * @param {string} fieldName - The name of the field to select.
     */
    function fieldSelected(ovEntry, fieldName) {
      var foundField = findField(ovEntry, fieldName);

      ovEntry.objectField = fieldName;
      ovEntry.fieldType = foundField.type;
      ovEntry.value = null;
      ovEntry.strategy = RESTORE_OVERWRITE_STRATEGIES.CONSTANT;
      ovEntry.strategyLabel = strategyNameToLabelMap[ovEntry.strategy];

      switch (foundField.type) {
        case SALESFORCE_FIELD_TYPES.PICKLIST:
        case SALESFORCE_FIELD_TYPES.MULTIPICKLIST:
          ovEntry.choices = [];
          addActivePicklistLabels(ovEntry.choices, ovEntry.labels, foundField.picklistValues);
          break;
        case SALESFORCE_FIELD_TYPES.BOOLEAN:
          ovEntry.value = '0';
          break;

        default:
          break;
      }

      selectionChanged();
    }

    /**
     * Finds the data for a named field in an overwrite value entry.
     *
     * @param {object} ovEntry - the overwrite value entry.
     * @param {string} fieldName - the name of the field to be found.
     */
    function findField(ovEntry, fieldName) {
      return _.find(ovEntry.allFields, function (field) {
        return field.name === fieldName;
      });
    }

    /**
     * Finds the picklist item for a field in an overwrite value object.
     *
     * @param {object} field - the field object.
     * @param {string} label - the label of the picklist item to be found.
     */
    function findPicklistItem(field, id) {
      return _.find(field.picklistValues, function (picklistValue) {
        return `${picklistValue.label}-${picklistValue.value}` === id;
      });
    }

    /**
     * Determines whether any presets have been defined.
     *
     * @returns {boolean} true if there are defined presets.
     */
    function hasPresets() {
      return model.presets.length > 0;
    }

    /**
     * Determines whether there are any restorable object types in the selected records.
     *
     * @returns {boolean} true if there are restorable object types.
     */
    function hasRestorableObjectTypes() {
      return model.allObjectTypes.length > 0;
    }

    /**
     * Decorates a single overwrite value with the data needed by this controller.
     *
     * @param {object} ovEntry - the overwrite value to be initialized.
     * @returns {Promise} a promise that is resolved when the value is initialized.
     */
    function initOverwriteValueEntry(ovEntry) {
      if (ovEntry.initialized) {
        return $q.when();
      }

      return $scope.loadAllFields(ovEntry).then(function () {
        if (!_.isArray(ovEntry.choices)) {
          ovEntry.choices = [];
          ovEntry.labels = [];
        }

        if (!_.isArray(ovEntry.selectedChoices)) {
          ovEntry.selectedChoices = [];
        }

        if (ovEntry.objectField) {
          var foundField = $scope.findField(ovEntry, ovEntry.objectField);
          ovEntry.fieldType = foundField.type;
          $scope.addActivePicklistLabels(ovEntry.choices, ovEntry.labels, foundField.picklistValues);
        }

        if (!ovEntry.strategy) {
          ovEntry.strategy = RESTORE_OVERWRITE_STRATEGIES.CONSTANT;
        }

        ovEntry.strategyLabel = strategyNameToLabelMap[ovEntry.strategy];
        ovEntry.initialized = true;
      });
    }

    /**
     * Decorates the overwrite values with the data needed by this controller
     *
     * @returns {Promise} a promise that is resolved when all values are initialized.
     */
    function initOverwriteValueEntries() {
      var promises = _.map(bulkRestoreData.getOverwriteValueEntries(), function (entry) {
        return $scope.initOverwriteValueEntry(entry);
      });
      return $q.all(promises);
    }

    /**
     * Determines whether the value is true for a boolean overwrite field. True is represented as "1" and false as "0",
     * maybe because that's how Salesforce wants it.
     *
     * @param {object} ovEntry - the overwrite value entry to be checked.
     * @returns {boolean} true if the value is "1", false otherwise.
     */
    function isBooleanFieldTrue(ovEntry) {
      return ovEntry.value === '1';
    }

    /**
     * Determines whether the input field for the field value is to be shown. It is shown only if a field is selected
     * and the strategy is CONSTANT.
     *
     * @param {object} ovEntry - the overwrite value entry to be checked.
     * @returns {boolean} true if the field value input is to be shown.
     */
    function isFieldValueShown(ovEntry) {
      return !!(ovEntry.objectField && ovEntry.strategy === RESTORE_OVERWRITE_STRATEGIES.CONSTANT);
    }

    /**
     * Determines whether the current preset has been modified.
     *
     * @returns {Boolean} true if the current preset has been modified, false otherwise.
     */
    function isPresetModified() {
      // If we're not using presets or no preset is selected, it's not modified
      if (!model.usingPreset || !model.currentPreset || model.initializingPreset) {
        return false;
      }

      // Compare the fields in the current preset with the current overwrite values
      var presetSize = model.currentPreset.fields.length;
      var ovEntries = bulkRestoreData.getOverwriteValueEntries();
      var valueCount = _.size(ovEntries);

      if (presetSize !== valueCount) {
        // If the lists are not the same length there's been a change
        return true;
      }

      for (var index = 0; index < valueCount; index++) {
        var presetField = model.currentPreset.fields[index];
        var ovEntry = ovEntries[index];

        // If the corresponding object types are not the same there's been a change
        if (presetField.objectType !== ovEntry.objectType) {
          return true;
        }

        // If the corresponding fields are not the same there's been a change
        if (presetField.objectField !== ovEntry.objectField) {
          return true;
        }

        // If the corresponding strategies are not the same there's been a change
        if (presetField.strategy !== ovEntry.strategy) {
          return true;
        }

        // If the corresponding values are not equivalent there's been a change
        if (!valuesAreEquivalent(presetField, ovEntry)) {
          return true;
        }
      }

      // We didn't find a change
      return false;
    }

    /**
     * Determines whether the values in a preset and an overwrite value entry are equivalent. Our Salesforce restore
     * code turns null, undefined, and '' into '#N/A', so they should be considered equivalent when checking for
     * changes to a preset.
     *
     * @param {object} presetField - the preset field to be checked.
     * @param {object} ovEntry - the overwrite value entry to be checked.
     * @returns {boolean} true if the values are equivalent, false otherwise.
     */
    function valuesAreEquivalent(presetField, ovEntry) {
      var pfIsBlank = isBlank(presetField.value);
      var ovIsBlank = isBlank(ovEntry.value);
      if (pfIsBlank && ovIsBlank) {
        return true;
      }

      // If they're not both "blank" we can do a straight up compare
      return presetField.value === ovEntry.value;
    }

    /**
     * Determines whether a value is "blank" as defined by null, undefined or ''. See the valuesAreEquivalent function.
     *
     * @param {*} value - the value to be checked.
     * @returns {boolean} true if the value is considered to be blank.
     */
    function isBlank(value) {
      return value === null || value === undefined || value === '';
    }

    /**
     * Loads the list of object types that will be available for selection.
     *
     * @returns {Promise} a promise that resolves when the list is loaded.
     */
    function loadObjectTypes() {
      // If the user wants to include children of deleted records in the restore we need to display all possible
      // object types because we don't know what the types of the children are.
      var promise;
      if (bulkRestoreData.isIncludingAllChildren()) {
        promise = BulkRestoreService.getObjectTypes({});
      } else {
        promise = BulkRestoreService.getSelectedObjectTypesWithRestorableFields(bulkRestoreData);
      }

      return promise.then(function (types) {
        model.allObjectTypes = _.sortBy(types, function (type) {
          return type.toLowerCase();
        });
      });
    }

    /**
     * Loads the list of all fields for an overwrite value.
     *
     * @param {object} ovEntry - the overwrite value entry whose fields are to be loaded.
     * @returns {Promise} a promise that resolves when the work is done.
     */
    function loadAllFields(ovEntry) {
      // If there is no type yet just set some defaults
      if (!ovEntry.objectType) {
        ovEntry.allFieldNames = [LOADING];
        return $q.when();
      }

      // Get the list of fields
      return BulkRestoreService.getFieldsForObjectTypes([ovEntry.objectType]).then(function (result) {
        const fields = result[ovEntry.objectType];
        // Build a sorted list of field names
        ovEntry.allFields = _.sortBy(fields, function (field) {
          return field.name.toLowerCase();
        });
        ovEntry.allFieldNames = _.pluck(ovEntry.allFields, 'name');
      });
    }

    /**
     * Called when a field value from a multi picklist is checked or unchecked.
     *
     * @param {object} ovEntry - The overwrite value entry.
     */
    function multiPickListSelectionChanged(ovEntry) {
      // Look up the field data
      var foundField = findField(ovEntry, ovEntry.objectField);

      // Build a list of the values of the selected items
      const values = _.map(ovEntry.selectedChoices, (id) => {
        // Look up the picklist item associated with the label
        const foundItem = findPicklistItem(foundField, id);
        return foundItem.value;
      });

      ovEntry.value = values.join(';');
    }

    /**
     * Called when a new object type is selected in an overwrite value row.
     *
     * @param {object} ovEntry - The overwrite value entry.
     * @param {string} type - The object type to select.
     * @returns {Promise} a promise that resolves when the work is done. We resolve with false if the selection was
     * rejected.
     */
    function objectTypeSelected(ovEntry, type) {
      // Make sure there are restorable fields. When we're including children we haven't pre-culled the list of object
      // types.
      return BulkRestoreService.getFieldsForObjectTypes([type]).then(function (result) {
        const fields = result[type];
        // Reset the overwrite value
        ovEntry.objectType = type;
        ovEntry.objectField = null;
        ovEntry.initialized = false;

        if (fields.length < 1) {
          // This shouldn't happen, but in case a new object type slips in with no createable or updateable fields we'll
          // catch it here. We should add it to spanalog BackupConstants.DELETED_OBJECT_IGNORE_LIST.
          //eslint-disable-next-line no-alert
          alert(
            `Object type "${type}" contains no restorable fields. Please let the Support team know via the KaseyaOne Portal that you saw this message.`
          );

          // There are no restorable fields for this type so remove it from the list
          model.allObjectTypes = _.without(model.allObjectTypes, type);
          ovEntry.objectType = null;
          return false;
        }

        return initOverwriteValueEntry(ovEntry).then(function () {
          selectionChanged();
          autoSelectField(ovEntry);
        });
      });
    }

    /**
     * Called when a field value from a picklist is selected.
     *
     * @param {object} ovEntry - The overwrite value entry.
     * @param {string} label - The label of the selected picklist item
     */
    function pickListValueSelected(ovEntry, id) {
      // Look up the field data
      var foundField = findField(ovEntry, ovEntry.objectField);
      // Look up the picklist item associated with the label
      var foundItem = findPicklistItem(foundField, id);

      ovEntry.id = id;
      ovEntry.value = foundItem.value;
    }

    /**
     * This should be called when a new object type or field is selected.
     */
    function selectionChanged() {
      $scope.calculateSelectedFieldsByObjectType();
      $scope.calculateDisabledObjectTypes();
    }

    /**
     * Determines whether the list of overwrite value entries should be shown.
     *
     * @returns {boolean} true if the list of overwrite value entries should be shown.
     */
    function shouldShowEntries() {
      return !model.usingPreset || (model.usingPreset && !!model.currentPreset && !model.initializingPreset);
    }

    /**
     * Toggles the value of a boolean overwrite field.
     *
     * @param {object} ovEntry - the overwrite value whose field setting is to be toggled.
     */
    function toggleBooleanField(ovEntry) {
      // Booleans are represented as '0' or '1'
      ovEntry.value = isBooleanFieldTrue(ovEntry) ? '0' : '1';
    }

    /**
     * Determines whether the overwrite values are valid. We disable the add button if not.
     *
     * @returns {boolean} - true if the overwrite values are not valid.
     */
    function valuesAreValid() {
      return bulkRestoreData.overwriteValuesAreValid();
    }

    /**
     * Saves the current preset.
     *
     * @returns {Promise} a promise that resolves when the preset is saved.
     */
    function savePreset() {
      var promise = $uibModal.open({
        templateUrl: 'bulkRestore/modal/saveModal.tpl.html',
        controller: 'SaveModalCtrl',
        windowClass: 'sbsf-save-modal-window',
        backdrop: 'static',
        resolve: {
          presets: function () {
            return model.presets;
          },
          presetName: function () {
            return model.currentPreset && model.currentPreset.name;
          }
        }
      }).result;

      return promise.then(function (result) {
        if (result.confirmed) {
          $rootScope.showLoadingMask = true;
          return BulkRestoreService.saveOverwriteValuePreset(
            result.presetName,
            bulkRestoreData.getOverwriteValueEntries()
          )
            .then(function (presets) {
              model.presets = presets;
              var preset = _.find(presets, function (preset) {
                return preset.name === result.presetName;
              });
              model.usingPreset = true;
              model.creatingNew = false;
              return $scope.selectPreset(preset, /*force:*/ true);
            })
            .catch(function (err) {
              ErrorService.report(err);
            })
            .finally(function () {
              $rootScope.showLoadingMask = false;
            });
        }
      });
    }

    /**
     * Handles selection of a preset
     *
     * @param {object} preset - the preset that was selected
     * @param {boolean} [force] - set to true if confirmation is not necessary
     * @returns {Promise} a promise that resolves when the work is done.
     */
    function selectPreset(preset, force) {
      var confirmPromise = force ? $q.when() : $scope.confirmUnsavedPreset();
      return confirmPromise
        .then(function () {
          model.initializingPreset = true;
          model.currentPreset = null;

          // Clear existing overwriteValues
          bulkRestoreData.clearOverwriteValueEntries();

          // Create an overwrite value entry for each preset field
          var promise = $q.when();
          _.each(preset.fields, function (presetField) {
            var ovEntry = _.clone(presetField);
            promise = promise
              .then(function () {
                return $scope.initOverwriteValueEntry(ovEntry);
              })
              .then(function () {
                bulkRestoreData.addOverwriteValueEntry(ovEntry);
              });
          });

          return promise;
        })
        .then(function () {
          if (!$scope.hasValues()) {
            return $scope.addEntry();
          }
        })
        .finally(function () {
          model.currentPreset = preset;
          model.initializingPreset = false;
        });
    }

    /**
     * Indicates whether the user is creating a new preset or using an existing one.
     *
     * @param {boolean} usePreset - true if the user wants to use an existing preset, false if they want to create a
     * new one.
     * @returns {Promise} a promise that resolves when the work is done.
     */
    function setUsingPreset(usePreset) {
      if (usePreset) {
        model.usingPreset = true;
        model.creatingNew = false;
        model.currentPreset = null;
        bulkRestoreData.clearOverwriteValueEntries();
        return $q.when();
      } else {
        // We're switching to Create New
        return $scope.confirmUnsavedPreset().then(function () {
          model.usingPreset = false;
          model.creatingNew = true;
          model.currentPreset = null;
          bulkRestoreData.clearOverwriteValueEntries();
          $scope.addEntry();
        });
      }
    }

    /**
     * Handles selection of an overwrite strategy for a field.
     *
     * @param {object} ovEntry - the overwrite value entry to be updated.
     * @param {string} strategyLabel - the label for the newly selected strategy.
     */
    function strategySelected(ovEntry, strategyLabel) {
      ovEntry.strategyLabel = strategyLabel;
      ovEntry.strategy = strategyLabelToNameMap[strategyLabel];
    }

    /**
     * Confirm loss of unsaved changes.
     *
     * @param {boolean} [goingToNext] - true if we're discarding changes because the user is moving forward in the wizard.
     * This affect the message we're going to show. Defaults to false.
     * @returns {Promise} a promise that is resolved when the user confirms or when confirmation is not needed. The
     * promise is rejected if the user does not confirm.
     */
    function confirmUnsavedPreset(goingToNext) {
      if (!bulkRestoreData.isOverwritingValues() || !$scope.isPresetModified()) {
        // Nothing to confirm
        return $q.when();
      }

      return $uibModal
        .open({
          templateUrl: 'shared/modal/confirmModal.tpl.html',
          controller: 'ConfirmModalCtrl',
          windowClass: 'sbsf-confirm-modal-window',
          backdrop: 'static',
          resolve: {
            confirmText: function () {
              var msgKey = goingToNext ? 'PRESET_UNSAVED_CHANGES_AND_CONTINUE' : 'PRESET_UNSAVED_CHANGES';
              return i18n(msgKey, model.currentPreset.name);
            },
            confirmTextKey: function () {},
            confirmButtonTextKey: function () {
              return 'CONFIRM';
            },
            titleTextKey: function () {
              return 'PRESET_MODIFIED';
            }
          }
        })
        .result.then(function (confirmed) {
          return confirmed ? $q.when() : $q.reject();
        });
    }

    /**
     * Utility method to check if we have overwrite values. This is needed since we will disable the inputs if we have
     * valid overwrite values but the overwrite values checkbox is unchecked.
     *
     * @returns {boolean} - true if we have overwrite values
     */
    function hasValues() {
      return bulkRestoreData.getOverwriteValueEntryCount() > 0;
    }

    /**
     * Loads the list of existing presets.
     *
     * @returns {Promise} a promise that is resolved when the presets are loaded.
     */
    function loadPresets() {
      $rootScope.showLoadingMask = true;
      return BulkRestoreService.getOverwriteValuePresets()
        .then(function (presets) {
          model.presets = presets;
        })
        .finally(function () {
          $rootScope.showLoadingMask = false;
        });
    }
  }
})();
